import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { IMetaConnectionState } from './types';

export const META_CONNECTION_QUERY_KEY = 'admin/ads/meta/connections';

export function useMetaConnectionQuery({
	levitateAccountId,
	enabled = true,
}: {
	levitateAccountId: string | null | undefined;
	enabled?: boolean;
}) {
	const userSession = useUserSession();
	return useQuery({
		queryFn: async () => {
			// queryFn never runs unless `enabled` (below) is true, which itself requires
			// `!!levitateAccountId`. Narrow the type here so the URL and the 404-synth
			// payload don't need either a `?? ''` fallback or a non-null assertion.
			if (!levitateAccountId) {
				throw new Error('useMetaConnectionQuery: queryFn invoked without a levitateAccountId');
			}
			try {
				return await userSession.webServiceHelper.callAsync<IMetaConnectionState>(
					`admin/ads/meta/connections/${encodeURIComponent(levitateAccountId)}`,
					'GET'
				);
			} catch (err) {
				// 404 from the projection means "no prior Meta connection for this
				// Levitate account" — synthesize an empty managed list so the assign
				// flow can create one rather than blocking Confirm indefinitely.
				if ((err as { systemCode?: number })?.systemCode === 404) {
					const synthetic: IMetaConnectionState = {
						levitateAccountId,
						managedAdAccountIds: [],
						hasCustomerToken: false,
					};
					return synthetic;
				}
				throw err;
			}
		},
		queryKey: [META_CONNECTION_QUERY_KEY, levitateAccountId],
		// Spec §Concurrency: always refetch on drawer open so we never write from a stale list.
		enabled: enabled && !!levitateAccountId,
		refetchOnMount: 'always',
		refetchOnWindowFocus: false,
		// Keep the cache around briefly so a drawer-close → reopen of the same
		// connection picks up the merged write from assign/unassign mutations
		// before the refetch lands.
		gcTime: 30_000,
		meta: {
			toastErrorMessage: "Couldn't load this Meta connection.",
			errorLogging: { fn: 'useMetaConnectionQuery' },
		},
	});
}
